import React from 'react';
import { clientsData } from '../mockData';
import { Building2, Coffee, Dumbbell, Briefcase, Utensils } from 'lucide-react';

export const ClientsSection = () => {
  const getIcon = (category) => {
    switch(category) {
      case 'Hospitality':
        return <Building2 className="w-8 h-8 text-[#FF6B00]" />;
      case 'Restaurant':
      case 'Restaurant Chain':
      case 'Fine Dining':
      case 'Food Court':
        return <Utensils className="w-8 h-8 text-[#FF6B00]" />;
      case 'Cafe Chain':
        return <Coffee className="w-8 h-8 text-[#FF6B00]" />;
      case 'Fitness':
        return <Dumbbell className="w-8 h-8 text-[#FF6B00]" />;
      default:
        return <Briefcase className="w-8 h-8 text-[#FF6B00]" />;
    }
  };

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #FF6B00 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header with 3D Effect */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#FF6B00]/10 backdrop-blur-md rounded-full border border-[#FF6B00]/30 mb-6">
            <div className="w-2 h-2 bg-[#FF6B00] rounded-full animate-pulse"></div>
            <span className="text-[#FF6B00] uppercase tracking-widest text-sm font-bold">Trusted By Industry Leaders</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-black mb-6">
            Our <span className="bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent">Esteemed Clients</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Partnering with top brands and businesses across hospitality, commercial, and residential sectors
          </p>
        </div>

        {/* 3D Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {clientsData.map((client, index) => (
            <div
              key={client.id}
              className="group relative bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#FF6B00] transition-all duration-500 hover:shadow-2xl hover:shadow-[#FF6B00]/20 cursor-pointer animate-fade-in-up"
              style={{ 
                animationDelay: `${index * 0.05}s`,
                transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
              }}
              onMouseMove={(e) => {
                const card = e.currentTarget;
                const rect = card.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const centerX = rect.width / 2;
                const centerY = rect.height / 2;
                const rotateX = ((y - centerY) / centerY) * -10;
                const rotateY = ((x - centerX) / centerX) * 10;
                card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;
              }}
              onMouseLeave={(e) => {
                const card = e.currentTarget;
                card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
              }}
            >
              {/* Icon */}
              <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300">
                {getIcon(client.category)}
              </div>

              {/* Client Name */}
              <h3 className="text-xl font-bold text-black mb-2 group-hover:text-[#FF6B00] transition-colors duration-300">
                {client.name}
              </h3>

              {/* Category Badge */}
              <div className="inline-block px-3 py-1 bg-gray-100 group-hover:bg-[#FF6B00]/10 rounded-full mb-3 transition-colors duration-300">
                <span className="text-xs font-semibold text-gray-600 group-hover:text-[#FF6B00] transition-colors duration-300">
                  {client.category}
                </span>
              </div>

              {/* Location */}
              <p className="text-sm text-gray-500">
                {client.location}
              </p>

              {/* Hover Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/0 via-[#FF6B00]/5 to-[#FF6B00]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none"></div>
              
              {/* Bottom Border Animation */}
              <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-[#FF6B00] to-[#E65F00] group-hover:w-full transition-all duration-500 rounded-b-2xl"></div>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 animate-fade-in-up animation-delay-500">
          <div className="text-center p-6 bg-gradient-to-br from-[#FF6B00]/10 to-transparent rounded-2xl border border-[#FF6B00]/20">
            <div className="text-4xl font-black bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent mb-2">14+</div>
            <div className="text-sm text-gray-600 font-semibold">Brands Served</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#FF6B00]/10 to-transparent rounded-2xl border border-[#FF6B00]/20">
            <div className="text-4xl font-black bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent mb-2">23+</div>
            <div className="text-sm text-gray-600 font-semibold">Projects Delivered</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#FF6B00]/10 to-transparent rounded-2xl border border-[#FF6B00]/20">
            <div className="text-4xl font-black bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent mb-2">100%</div>
            <div className="text-sm text-gray-600 font-semibold">Client Satisfaction</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#FF6B00]/10 to-transparent rounded-2xl border border-[#FF6B00]/20">
            <div className="text-4xl font-black bg-gradient-to-r from-[#FF6B00] to-[#E65F00] bg-clip-text text-transparent mb-2">Pan India</div>
            <div className="text-sm text-gray-600 font-semibold">Presence</div>
          </div>
        </div>
      </div>
    </section>
  );
};
